/**
 * The facet buckets an enum or boolean column's header dropdown renders, and the shared facet state it marks
 * itself against. Shared by both overview containers, because a value labelled or ticked one way in the
 * column header and another way in the filter panel is one filter with two answers.
 */

import { BehaviorSubject, combineLatest, defer, map, Observable, take } from 'rxjs';

import { optionalTranslate, SharedLanguageService } from '@language';

import {
    ApiQuery,
    FacetProfile,
    FacetProfileField,
    FacetSelectionType,
    FieldDefinition,
    FieldType,
    QueryPartData,
    QueryPartStrategy,
    ShFacet,
    ShFacetDataService,
    ShSelectableFacetValue,
    ShSelectedFacetValue,
    ShUrlQueryService,
    StructuredQuery
} from '@stylehaven/components';

/** The selected facet values, grouped by field: what pre-fills a dropdown and drives its active indicator. */
export function groupFacetsByField(selectedFacets: ShSelectedFacetValue[]): QueryPartData[] {
    const valuesByKey: Map<string, string[]> = new Map<string, string[]>();

    selectedFacets
        .filter(facet => !facet.missing)
        .forEach(facet => {
            const values: string[] = valuesByKey.get(facet.key) ?? [];
            values.push(facet.value);
            valuesByKey.set(facet.key, values);
        });

    return [...valuesByKey.entries()].map(([field, values]) => ({
        field,
        strategy: QueryPartStrategy.MatchAll,
        values
    }));
}

/** Ticks the loaded buckets from the shared facet state, so the header and the panel agree on what is on. */
export function markSelectedFacetValues(
    loaded: ReadonlyMap<string, ShFacet>,
    selectedFacets: ShSelectedFacetValue[]
): Map<string, ShFacet> {
    const marked: Map<string, ShFacet> = new Map<string, ShFacet>();

    loaded.forEach((facet, key) => {
        const selectedValues: Set<string> = new Set(
            selectedFacets.filter(selected => selected.key === key).map(selected => selected.value)
        );

        const values: ShSelectableFacetValue[] = facet.values.map(facetValue => ({
            ...facetValue,
            selected: selectedValues.has(facetValue.value)
        }));

        marked.set(key, { ...facet, values });
    });

    return marked;
}

/** Labels a bucket the way the rest of the app labels that field's values, falling back to the raw value. */
export function translateFacetValues(
    facet: ShFacet,
    dottedKey: string,
    fieldDefinitions: FieldDefinition[],
    translateService: SharedLanguageService
): ShFacet {
    const flatKey: string =
        fieldDefinitions.find(fieldDefinition => fieldDefinition.DottedKey === dottedKey)?.FlatKey ?? dottedKey;

    const values: ShSelectableFacetValue[] = facet.values.map(facetValue => ({
        ...facetValue,
        label: optionalTranslate(translateService, flatKey, facetValue.value)
    }));

    return { ...facet, values };
}

/**
 * A boolean field's two buckets, always both, counted from what came back. The engine returns only the values
 * that occur, and a dropdown offering just *Ja* cannot express "show me the others".
 */
export function buildBooleanFacet(
    field: FacetProfileField,
    loadedFacet: ShFacet,
    translateService: SharedLanguageService
): ShFacet {
    const countFor: (value: string) => number = value =>
        loadedFacet.values.find(facetValue => facetValue.value === value)?.count ?? 0;

    const booleanValue: (value: string, translationKey: 'true' | 'false') => ShSelectableFacetValue = (
        value,
        translationKey
    ) => ({
        value,
        label: translateService.instant(`overrides.BooleanField.${translationKey}`),
        count: countFor(value),
        selectionType: field.SelectionType
    });

    return {
        key: field.DottedKey,
        label: '',
        hasMoreValues: false,
        selectionType: field.SelectionType,
        values: [booleanValue('1', 'true'), booleanValue('0', 'false')]
    };
}

export function withoutOwnFacet(query: ApiQuery, dottedKey: string): ApiQuery {
    const structuredQuery: StructuredQuery = {
        ...query.structuredQuery,
        Facets: (query.structuredQuery.Facets ?? []).filter(facet => facet.DottedKey !== dottedKey)
    };

    return new ApiQuery(structuredQuery, query.restriction, query.fieldSelect);
}

/** The empty facet that stands for one field, which is how a dropdown's reset clears everything it selected. */
export function emptyFacetFor(dottedKey: string): ShFacet {
    return {
        key: dottedKey,
        label: '',
        hasMoreValues: false,
        values: [],
        selectionType: FacetSelectionType.MUST
    };
}

/**
 * Which enum columns may carry a header dropdown: only fields the filter panel also lists, and (HFBD-5) only
 * those it is actually showing — the two are one filter with two front doors.
 *
 * `visibleKeys` of `null` means **no panel has reported**, which is not the same as a panel reporting none.
 * In condensed mode no panel renders at all yet the table is still fed this list, so that case keeps every
 * dropdown; collapsing the two would silently strip an embedded overview of all its filters.
 *
 * A field the user is filtering on keeps its dropdown whatever the panel reports. The panel's own exemption
 * cannot cover this: at zero results the engine returns no facets, so the active field is absent from the
 * response and the panel truthfully reports it is showing nothing — which would strand the user with no way
 * to undo the filter from the header.
 */
export function toEnumFacetFieldKeys(
    profileKeys: string[],
    visibleKeys: readonly string[] | null,
    activeFilters: QueryPartData[]
): string[] {
    if (visibleKeys === null) {
        return profileKeys;
    }

    const activeKeys: Set<string> = new Set(activeFilters.map(filter => filter.field));

    return profileKeys.filter(key => visibleKeys.includes(key) || activeKeys.has(key));
}

/** Everything one bucket load needs to know about the page asking for it. */
export interface ColumnEnumFilterSource {
    /** The query the filter panel runs, so a column's buckets match the panel's. */
    readonly query: ApiQuery;
    readonly facetProfile?: FacetProfile;
    readonly fieldDefinitions: FieldDefinition[];
}

/** One overview's enum and boolean column dropdowns: their buckets, and the selections they write back. */
export interface ColumnEnumFilter {
    /** The loaded buckets, ticked from the shared facet state so the header and the panel agree. */
    readonly facets$: Observable<ReadonlyMap<string, ShFacet>>;
    /** Loads the live buckets for one field. A field the profile does not list has no dropdown to fill. */
    load(dottedKey: string, source: ColumnEnumFilterSource): void;
    /** Toggles one value: a single-select field is replaced, a multi-select one added to, a selected one removed. */
    toggleValue(facetValue: ShSelectedFacetValue): void;
    /** Clears every selected value of one field — the dropdown's reset affordance. */
    clear(dottedKey: string): void;
}

/**
 * Binds the enum-column dropdowns of one overview to the shared facet state.
 *
 * A plain factory rather than a service, following `facetOrderForView`: it is created per container with
 * that container's own facet data service — the record overviews read `v2/records/facets` and the Taken
 * pages `v2/tasks/facets` — while everything above that endpoint is the same on both.
 */
export function columnEnumFilterFor(
    facetDataService: ShFacetDataService,
    urlQueryService: ShUrlQueryService,
    translateService: SharedLanguageService
): ColumnEnumFilter {
    const loaded$: BehaviorSubject<ReadonlyMap<string, ShFacet>> = new BehaviorSubject<ReadonlyMap<string, ShFacet>>(
        new Map<string, ShFacet>()
    );

    return {
        // `defer`, so the facet state is read when a caller first subscribes rather than when the binding is
        // created — a container builds this before its own streams exist.
        facets$: defer(() =>
            combineLatest([loaded$, urlQueryService.getSelectedFacets()]).pipe(
                map(([loaded, selectedFacets]) => markSelectedFacetValues(loaded, selectedFacets))
            )
        ),

        load(dottedKey: string, source: ColumnEnumFilterSource): void {
            const field: FacetProfileField | undefined = source.facetProfile?.Fields?.find(
                profileField => profileField.DottedKey === dottedKey
            );

            if (!field) {
                return;
            }

            const isBoolean: boolean =
                source.fieldDefinitions.find(fieldDefinition => fieldDefinition.DottedKey === dottedKey)?.Type ===
                FieldType.BooleanField;

            facetDataService
                .getProfileFieldFacet(withoutOwnFacet(source.query, dottedKey), field, { StartIndex: 0 }, true)
                .pipe(take(1))
                .subscribe(facet => {
                    const updated: Map<string, ShFacet> = new Map(loaded$.value);

                    updated.set(
                        dottedKey,
                        isBoolean
                            ? buildBooleanFacet(field, facet, translateService)
                            : translateFacetValues(facet, dottedKey, source.fieldDefinitions, translateService)
                    );
                    loaded$.next(updated);
                });
        },

        toggleValue(facetValue: ShSelectedFacetValue): void {
            urlQueryService
                .getSelectedFacets()
                .pipe(take(1))
                .subscribe(selectedFacets => {
                    const isSelected: boolean = selectedFacets.some(
                        selected => selected.key === facetValue.key && selected.value === facetValue.value
                    );

                    if (isSelected) {
                        urlQueryService.removeFacetValue(facetValue);
                    } else if (facetValue.selectionType === FacetSelectionType.MUST) {
                        urlQueryService.replaceFacetValues(emptyFacetFor(facetValue.key), [facetValue]);
                    } else {
                        urlQueryService.addFacetValue(facetValue);
                    }
                });
        },

        clear(dottedKey: string): void {
            urlQueryService.replaceFacetValues(emptyFacetFor(dottedKey), []);
        }
    };
}
